import React, { useEffect, useMemo, useState, useRef } from 'react'
import { InputField, Button } from '@components/common'
import { NoteContextType } from '@dataTypes/note.type'
import { NOTE_TAG_OPTIONS } from '@components/common/TagBadge/TagBadge.component'
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns'
import { DatePickerComponent } from '@syncfusion/ej2-react-calendars'

interface AddNoteFilterProps {
  searchTerm: string
  onSearchChange: (term: string) => void
  onApply: (
    filters: {
      filterBy: string
      dateFrom: string
      dateTo: string
      category: string
      item: string
    },
    resetActiveNote?: boolean,
  ) => void
  refList?:
    | {
        context_type: NoteContextType | string
        context_id: string | null
        context_name?: string
        context_details?: Record<string, unknown> | null
        refName?: string
      }[]
    | undefined
  initialFilter?: {
    filterBy?: string
    dateFrom?: string
    dateTo?: string
    category?: string
    item?: string
  }
}

const AddNoteFilter: React.FC<AddNoteFilterProps> = ({
  searchTerm,
  onSearchChange,
  onApply,
  refList,
  initialFilter,
}) => {
  const getStringFromDetails = (
    details: Record<string, unknown> | null | undefined,
    key: string,
  ): string | null => {
    if (!details) {
      return null
    }

    const value = details[key]
    return typeof value === 'string' && value.trim() ? value : null
  }

  const getContextDetailsDisplayName = (
    details: Record<string, unknown> | null | undefined,
  ): string | null => {
    const title =
      getStringFromDetails(details, 'title') ??
      getStringFromDetails(details, 'name') ??
      getStringFromDetails(details, 'display_name')

    if (title) {
      return title
    }

    const appointmentDate = getStringFromDetails(details, 'appt_date')
    const appointmentStart = getStringFromDetails(details, 'appt_start')
    if (appointmentDate && appointmentStart) {
      return `${appointmentDate} - ${appointmentStart}`
    }

    return null
  }

  const [showFilters, setShowFilters] = useState<boolean>(false)
  const [filterBy, setFilterBy] = useState<string | null>(
    initialFilter?.filterBy ?? null,
  )
  const [dateFrom, setDateFrom] = useState<string>(
    initialFilter?.dateFrom ?? '',
  )
  const [dateTo, setDateTo] = useState<string>(initialFilter?.dateTo ?? '')
  const [category, setCategory] = useState<string | null>(
    initialFilter?.category ?? null,
  )
  const [item, setItem] = useState<string | null>(initialFilter?.item ?? null)
  const filterRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (initialFilter !== undefined) {
      setFilterBy(initialFilter.filterBy ?? null)
      setDateFrom(initialFilter.dateFrom ?? '')
      setDateTo(initialFilter.dateTo ?? '')
      setCategory(initialFilter.category ?? null)
      setItem(initialFilter.item ?? null)
    }
  }, [
    initialFilter?.filterBy,
    initialFilter?.dateFrom,
    initialFilter?.dateTo,
    initialFilter?.category,
    initialFilter?.item,
  ])

  const activeFiltersCount = [
    filterBy,
    dateFrom,
    dateTo,
    category,
    item,
  ].filter((filter) => filter && filter !== '').length

  const resetFilters = (): void => {
    setFilterBy(null)
    setDateFrom('')
    setDateTo('')
    setCategory(null)
    setItem(null)
  }

  const applyFilters = (): void => {
    onApply(
      {
        filterBy: filterBy ?? '',
        dateFrom,
        dateTo,
        category: category ?? '',
        item: item ?? '',
      },
      true,
    )
    setShowFilters(false)
  }

  const categoriesWithNotes = useMemo(
    () =>
      new Set(
        (refList ?? [])
          .map((r) => r.context_type)
          .filter((t): t is string => Boolean(t)),
      ),
    [refList],
  )

  const noteCategories: { value: string; text: string }[] = [
    { value: '', text: 'Select category' },
    ...NOTE_TAG_OPTIONS.filter((opt) => categoriesWithNotes.has(opt.value)).map(
      (opt) => ({ value: opt.value, text: opt.text }),
    ),
  ]

  useEffect(() => {
    if (category && !categoriesWithNotes.has(category)) {
      setCategory(null)
    }
  }, [category, categoriesWithNotes])

  const noteItems: { value: string; text: string; context_type?: string }[] = [
    { value: '', text: 'Select item' },
    ...(refList
      ?.filter((p) => !category || p.context_type === category)
      .map((p) => ({
        value: p.context_id ?? '',
        text:
          p.context_name ??
          getContextDetailsDisplayName(p.context_details) ??
          p.refName ??
          'Unnamed item',
        context_type: !category ? p.context_type : undefined,
      })) ?? []),
  ]

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as HTMLElement

      // Check if click is inside the filter dropdown
      if (filterRef.current && filterRef.current.contains(target)) {
        return
      }

      // Check if click is on Syncfusion dropdown elements
      if (
        target.closest('.e-dropdownlist') ||
        target.closest('.e-popup') ||
        target.closest('.e-list-parent') ||
        target.closest('.e-ddl')
      ) {
        return
      }

      // If we reach here and filters are shown, close them
      if (showFilters) {
        setShowFilters(false)
      }
    }

    if (showFilters) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showFilters])

  return (
    <div className="position-relative w-100">
      <div className="d-flex align-items-center note-filter-search-box">
        <i
          className="mgc mgc_search_2_line add-note-filter-search-icon-left"
          aria-hidden
        />
        <InputField
          placeholder="Search notes..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
        />
        <button
          type="button"
          className="unstyled-button add-note-filter-icon-btn position-relative"
          onClick={() => setShowFilters((prev) => !prev)}
          title="Filter notes"
          aria-label="Filter notes"
        >
          <i className="mgc mgc_settings_2_line" aria-hidden />
          {activeFiltersCount > 0 && (
            <span
              className="position-absolute rounded-circle bg-danger"
              style={{ top: '0', right: '-2px', width: '5px', height: '5px' }}
            />
          )}
        </button>
      </div>

      {showFilters && (
        <div
          ref={filterRef}
          className="note-editor-filter-box border rounded p-2 mt-2 bg-white shadow position-absolute"
        >
          <div className="mb-1">
            <div className="d-flex flex-row justify-content-between mb-1">
              <div className="filter-label">Filter</div>
              {filterBy && (
                <div
                  className="filter-label-reset"
                  onClick={() => {
                    setFilterBy(null)
                  }}
                >
                  Reset
                </div>
              )}
            </div>
            <div>
              <DropDownListComponent
                dataSource={[
                  { value: '', text: 'Select search filter' },
                  { value: 'title', text: 'Title' },
                  { value: 'note', text: 'Note' },
                ]}
                fields={{ text: 'text', value: 'value' }}
                placeholder="Select search filter"
                value={filterBy ?? null}
                change={(e) => setFilterBy(e.value as string)}
              />
            </div>
          </div>

          <div className="mb-1 gx-1 d-flex justify-content-between flex-row">
            <div className="d-flex flex-column w-100">
              <div className="d-flex flex-row justify-content-between mb-1">
                <div className="filter-label">Date</div>
                {(dateFrom || dateTo) && (
                  <div
                    className="filter-label-reset"
                    onClick={() => {
                      setDateFrom('')
                      setDateTo('')
                    }}
                  >
                    Reset
                  </div>
                )}
              </div>
              <div className="d-flex flex-row justify-content-between gap-1">
                <DatePickerComponent
                  value={dateFrom ? new Date(dateFrom) : undefined}
                  placeholder="From"
                  change={(e) => setDateFrom(e.value as string)}
                  format="MM/dd/yyyy"
                  showClearButton={false}
                />
                <DatePickerComponent
                  value={dateTo ? new Date(dateTo) : undefined}
                  placeholder="To"
                  change={(e) => setDateTo(e.value as string)}
                  format="MM/dd/yyyy"
                  showClearButton={false}
                />
              </div>
            </div>
          </div>

          <div className="mb-1">
            <div className="d-flex flex-row justify-content-between mb-1">
              <div className="filter-label">Category</div>
              {category && (
                <div
                  className="filter-label-reset"
                  onClick={() => {
                    setCategory(null)
                  }}
                >
                  Reset
                </div>
              )}
            </div>
            <div>
              <DropDownListComponent
                dataSource={noteCategories}
                fields={{ text: 'text', value: 'value' }}
                placeholder="Select category"
                value={category ?? null}
                change={(e) => setCategory(e.value as string)}
              />
            </div>
          </div>

          <div className="mb-1">
            <div className="d-flex flex-row justify-content-between mb-1">
              <div className="filter-label">Item</div>
              {item && (
                <div
                  className="filter-label-reset"
                  onClick={() => {
                    setItem(null)
                  }}
                >
                  Reset
                </div>
              )}
            </div>
            <div>
              <DropDownListComponent
                dataSource={noteItems}
                fields={{ text: 'text', value: 'value' }}
                placeholder="Select item"
                value={item ?? null}
                change={(e) => setItem(e.value as string)}
                itemTemplate={(data: {
                  text: string
                  context_type: string
                }) => (
                  <div className="d-flex flex-row justify-content-between">
                    <span className="text-truncate">{data.text}</span>
                    <span className="text-muted">{data.context_type}</span>
                  </div>
                )}
              />
            </div>
          </div>

          <div className="mt-3">
            <div className="d-flex justify-content-end">
              <Button className="button-secondary me-1" onClick={resetFilters}>
                Reset All
              </Button>
              <Button className="button-primary" onClick={applyFilters}>
                Apply
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AddNoteFilter
